// ============================================================================
// Member Service
// Fetches member and vehicle data from the admin-backend API
// ============================================================================

import { fetchWithAuth } from './auth';
import { Member, PassengerVehicle, RecreationalVehicle } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8004/api';

// ============================================================================
// API Response Types (match backend Pydantic schemas — snake_case)
// ============================================================================

export interface ApiMember {
  id: number;
  corporate_id: number;
  first_name: string;
  last_name: string;
  guest_first_name?: string;
  guest_last_name?: string;
  username: string;
  email: string;
  main_phone?: string;
  second_phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  emergency_name?: string;
  emergency_phone?: string;
  emergency_relation?: string;
  banned: boolean;
  last_login_date?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface ApiPassengerVehicle {
  id: number;
  member_id: number;
  license_no: string;
  make?: string;
  model?: string;
  color?: string;
  active: boolean;
  display_order: number;
  created_at?: string;
  updated_at?: string;
}

export interface ApiRecreationalVehicle {
  id: number;
  member_id: number;
  store_id: number;
  type: string;
  description?: string;
  dimensions?: {
    unit?: string;
    length?: number;
    width?: number;
    height?: number;
  };
  license_plate?: string;
  amp?: number;
  length?: number;
  active: boolean;
  display_order: number;
  created_at?: string;
  updated_at?: string;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetch all members, optionally filtered by corporate_id.
 */
export async function getMembers(corporateId?: number): Promise<ApiMember[]> {
  const params = new URLSearchParams();
  if (corporateId) params.append('corporate_id', corporateId.toString());
  const qs = params.toString();
  return fetchWithAuth(`${API_BASE_URL}/members/${qs ? '?' + qs : ''}`);
}

/**
 * Fetch a single member by ID.
 */
export async function getMemberById(memberId: number): Promise<ApiMember> {
  return fetchWithAuth(`${API_BASE_URL}/members/${memberId}`);
}

/**
 * Fetch passenger vehicles for a given member.
 */
export async function getPassengerVehicles(memberId: number): Promise<ApiPassengerVehicle[]> {
  return fetchWithAuth(`${API_BASE_URL}/member-passenger-vehicles/?member_id=${memberId}`);
}

/**
 * Fetch recreational vehicles for a given member.
 */
export async function getRecreationalVehicles(memberId: number): Promise<ApiRecreationalVehicle[]> {
  return fetchWithAuth(`${API_BASE_URL}/member-recreational-vehicles/?member_id=${memberId}`);
}

// ============================================================================
// Create / Update / Delete Types
// ============================================================================

export interface MemberCreate {
  first_name: string;
  last_name: string;
  email: string;
  username: string;
  password: string;
  corporate_code?: string | number;
  main_phone?: string;
  second_phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  banned?: boolean;
  guest_first_name?: string;
  guest_last_name?: string;
  emergency_name?: string;
  emergency_phone?: string;
  emergency_relation?: string;
  notes?: string;
}

export interface MemberUpdate {
  first_name?: string;
  last_name?: string;
  email?: string;
  username?: string;
  password?: string;
  main_phone?: string;
  second_phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  banned?: boolean;
  guest_first_name?: string;
  guest_last_name?: string;
  emergency_name?: string;
  emergency_phone?: string;
  emergency_relation?: string;
  notes?: string;
}

// Passenger Vehicle Create/Update
export interface PassengerVehicleCreate {
  license_no?: string;
  make?: string;
  model?: string;
  color?: string;
  display_order?: number;
}

export interface PassengerVehicleUpdate {
  license_no?: string;
  make?: string;
  model?: string;
  color?: string;
  display_order?: number;
  active?: boolean;
}

// Recreational Vehicle Create/Update
export interface RecreationalVehicleCreate {
  type: string;
  description?: string;
  license_plate?: string;
  amp?: number;
  length?: number;
  display_order?: number;
}

export interface RecreationalVehicleUpdate {
  type?: string;
  description?: string;
  license_plate?: string;
  amp?: number;
  length?: number;
  display_order?: number;
  active?: boolean;
}

// ============================================================================
// Create / Update / Delete API Functions
// ============================================================================

export async function createMember(data: MemberCreate): Promise<ApiMember> {
  return fetchWithAuth(`${API_BASE_URL}/members/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateMember(memberId: number, data: MemberUpdate): Promise<ApiMember> {
  return fetchWithAuth(`${API_BASE_URL}/members/${memberId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteMember(memberId: number): Promise<void> {
  return fetchWithAuth(`${API_BASE_URL}/members/${memberId}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Passenger Vehicle CRUD
// ============================================================================

export async function createPassengerVehicle(
  memberId: number,
  data: PassengerVehicleCreate
): Promise<ApiPassengerVehicle> {
  return fetchWithAuth(
    `${API_BASE_URL}/member-passenger-vehicles/?member_id=${memberId}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function updatePassengerVehicle(
  vehicleId: number,
  data: PassengerVehicleUpdate
): Promise<ApiPassengerVehicle> {
  return fetchWithAuth(
    `${API_BASE_URL}/member-passenger-vehicles/${vehicleId}`,
    { method: 'PUT', body: JSON.stringify(data) }
  );
}

export async function deletePassengerVehicle(vehicleId: number): Promise<void> {
  return fetchWithAuth(
    `${API_BASE_URL}/member-passenger-vehicles/${vehicleId}`,
    { method: 'DELETE' }
  );
}

// ============================================================================
// Recreational Vehicle CRUD
// ============================================================================

export async function createRecreationalVehicle(
  memberId: number,
  data: RecreationalVehicleCreate
): Promise<ApiRecreationalVehicle> {
  return fetchWithAuth(
    `${API_BASE_URL}/member-recreational-vehicles/?member_id=${memberId}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function updateRecreationalVehicle(
  vehicleId: number,
  data: RecreationalVehicleUpdate
): Promise<ApiRecreationalVehicle> {
  return fetchWithAuth(
    `${API_BASE_URL}/member-recreational-vehicles/${vehicleId}`,
    { method: 'PUT', body: JSON.stringify(data) }
  );
}

export async function deleteRecreationalVehicle(vehicleId: number): Promise<void> {
  return fetchWithAuth(
    `${API_BASE_URL}/member-recreational-vehicles/${vehicleId}`,
    { method: 'DELETE' }
  );
}

// ============================================================================
// Mappers: API Response → Frontend Types
// ============================================================================

export function mapApiMember(api: ApiMember): Member {
  return {
    id: api.id,
    corporateId: api.corporate_id,
    username: api.username,
    firstName: api.first_name,
    lastName: api.last_name,
    email: api.email,
    phone: api.main_phone || '',
    secondPhone: api.second_phone,
    address: api.address || '',
    city: api.city || '',
    state: api.state || '',
    zipCode: api.zip_code || '',
    country: api.country,
    emergencyName: api.emergency_name,
    emergencyPhone: api.emergency_phone,
    emergencyRelation: api.emergency_relation,
    banned: api.banned,
    lastLoginDate: api.last_login_date,
    notes: api.notes,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

export function mapApiPassengerVehicle(api: ApiPassengerVehicle): PassengerVehicle {
  return {
    id: api.id,
    memberId: api.member_id,
    licenseNo: api.license_no,
    make: api.make,
    model: api.model,
    color: api.color,
    active: api.active,
  };
}

export function mapApiRecreationalVehicle(api: ApiRecreationalVehicle): RecreationalVehicle {
  return {
    id: api.id,
    memberId: api.member_id,
    storeId: api.store_id,
    type: api.type,
    description: api.description,
    licensePlate: api.license_plate,
    amp: api.amp,
    length: api.length,
    active: api.active,
  };
}
